// useScrapers.ts - React Query hooks for scraper data
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';

export interface ScraperStatus {
  source: string;
  state: {
    last_run_at: number | null;
    last_success_at: number | null;
    updated_at: number;
  } | null;
  lastRun: {
    id: string;
    status: string;
    ads_found: number;
    ads_new: number;
    ads_modified: number;
    ads_failed?: number;
    started_at: number;
    completed_at: number | null;
  } | null;
}

export interface RunHistory {
  id: string;
  source: string;
  status: string;
  started_at: number;
  completed_at: number | null;
  ads_found: number;
  ads_new: number;
  ads_modified: number;
  ads_removed: number;
  ads_processed: number;
  ads_failed: number;
  error: string | null;
}

/**
 * Get all scraper statuses
 */
export function useScrapers() {
  return useQuery<ScraperStatus[]>({
    queryKey: ['scrapers'],
    queryFn: async () => {
      const response = await api.scrapers.getAll();
      return response.data || [];
    },
    refetchInterval: 10000, // Refetch every 10 seconds
  });
}

/**
 * Get specific scraper status
 */
export function useScraperStatus(source: string) {
  return useQuery<ScraperStatus>({
    queryKey: ['scrapers', source],
    queryFn: async () => {
      const response = await api.scrapers.getStatus(source);
      return response.data;
    },
    enabled: !!source,
    refetchInterval: 10000,
  });
}

/**
 * Get scraper run history
 */
export function useScraperHistory(source: string, limit = 50) {
  return useQuery<RunHistory[]>({
    queryKey: ['scrapers', source, 'history', limit],
    queryFn: async () => {
      const response = await api.scrapers.getHistory(source, limit);
      return response.data || [];
    },
    enabled: !!source,
  });
}

/**
 * Mutation to run a scraper
 */
export function useRunScraper() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (source: string) => {
      const response = await api.scrapers.run(source);
      return response.data;
    },
    onSuccess: (_, source) => {
      // Invalidate and refetch scraper status
      queryClient.invalidateQueries({ queryKey: ['scrapers', source] });
      queryClient.invalidateQueries({ queryKey: ['scrapers'] });
    },
  });
}

/**
 * Mutation to run all scrapers
 */
export function useRunAllScrapers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sources?: string[]) => {
      const response = await api.scrapers.runAll(sources);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate all scraper queries
      queryClient.invalidateQueries({ queryKey: ['scrapers'] });
    },
  });
}

/**
 * Mutation to cleanup old scraper data
 */
export function useCleanupScrapers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (daysToKeep?: number) => {
      const response = await api.scrapers.cleanup(daysToKeep);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scrapers'] });
    },
  });
}
